/**
 * Pac-Man Mode Types
 * Types for the Pac-Man mini-game energizer
 */

// ============================================================================
// CONSTANTS
// ============================================================================

export const MAZE_WIDTH = 21;
export const MAZE_HEIGHT = 13;
export const TILE_SIZE = 32;
export const PACMAN_SPEED = 4;
export const GHOST_SPEED = 2;
export const CHASE_MAX_DURATION = 3000; // 3 seconds max
export const POPUP_DURATION = 1500;
export const COLLISION_THRESHOLD = 20; // pixels

// ============================================================================
// BASE TYPES
// ============================================================================

export interface GridPosition {
  x: number;
  y: number;
}

export interface PixelPosition {
  x: number;
  y: number;
}

export type MovementAxis = 'HORIZONTAL' | 'VERTICAL';
export type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
export type CellType = 'WALL' | 'PATH' | 'DOT';

// ============================================================================
// MAZE
// ============================================================================

export interface MazeCell {
  type: CellType;
  position: GridPosition;
}

export interface GhostSpawnPoint {
  position: GridPosition;
  axis: MovementAxis;
}

export interface Maze {
  width: number;
  height: number;
  cells: MazeCell[][];
  pacmanSpawn: GridPosition;
  ghostSpawns: GhostSpawnPoint[];
}

// ============================================================================
// ENTITIES
// ============================================================================

export interface PacmanState {
  position: PixelPosition;
  gridPosition: GridPosition;
  direction: Direction;
  mouthOpen: boolean;
}

export interface GhostState {
  id: string;
  memberId: string;
  position: PixelPosition;
  gridPosition: GridPosition;
  axis: MovementAxis;
  velocity: number; // +1 or -1
  isCaptured: boolean;
}

// ============================================================================
// GAME PHASES
// ============================================================================

export type PacmanPhase =
  | 'IDLE'
  | 'CHASE'
  | 'CAPTURE'
  | 'POPUP'
  | 'TRANSITION';

// ============================================================================
// GAME STATE
// ============================================================================

export interface PacmanGameState {
  phase: PacmanPhase;
  maze: Maze | null;
  pacman: PacmanState | null;
  ghosts: GhostState[];
  capturedMemberId: string | null;
  chaseStartTime: number | null;
  captureOrder: string[];
  isLastGhost: boolean;
  fireworksTriggered: boolean;
}

// ============================================================================
// ACTIONS
// ============================================================================

export type PacmanAction =
  | { type: 'INIT_GAME'; maze: Maze; memberIds: string[] }
  | { type: 'START_CHASE' }
  | { type: 'UPDATE_POSITIONS'; pacman: PacmanState; ghosts: GhostState[] }
  | { type: 'CAPTURE_GHOST'; ghostId: string }
  | { type: 'SHOW_POPUP' }
  | { type: 'GO_TO_KANBAN' }
  | { type: 'RETURN_TO_MAZE' }
  | { type: 'TRIGGER_FIREWORKS' }
  | { type: 'SYNC_CAPTURED_GHOSTS'; capturedMemberIds: string[] };
